import { type InferResponseType, unwrap } from "@nexxt/api-client";
import type { ListIntegrationErrorsInput, ListReconciliationIssuesInput } from "@nexxt/contracts/integration";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the integration error queue (M16, FR-M16-04): the rows by cursor, and how many there are of each
// problem; what travelled with TIL about the entity of a row, and the body of one message (ADR-164); what the domains
// find wrong towards Business Central (FR-M16-03, ADR-177).

type ListPage = InferResponseType<typeof api.integration.errors.$get, 200>;
export type IntegrationError = ListPage["items"][number];
export type IntegrationErrorSummary = InferResponseType<typeof api.integration.errors.summary.$get, 200>;
export type MessageLogEntry = InferResponseType<typeof api.integration.messages.$get, 200>["items"][number];
export type ReconciliationIssue = InferResponseType<typeof api.integration.reconciliation.$get, 200>["items"][number];
export type ReconciliationSummary = InferResponseType<typeof api.integration.reconciliation.summary.$get, 200>;
export type ReconciliationFilters = Pick<ListReconciliationIssuesInput, "kind" | "entityType" | "state">;

/** The latest messages of an entity the panel shows: the older ones are in the log, not here. */
export const ENTITY_MESSAGES_SHOWN = 50;

export type IntegrationErrorFilters = Pick<ListIntegrationErrorsInput, "problem" | "kind">;

const keys = { all: ["integration-errors"] as const };

const summary = queryOptions({
  queryKey: [...keys.all, "summary"],
  queryFn: ({ signal }) => unwrap(api.integration.errors.summary.$get({}, { init: { signal } })),
});

export const integrationQueries = {
  ...keys,

  list: ({ problem, kind }: IntegrationErrorFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "list", { problem, kind }],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          api.integration.errors.$get(
            {
              query: {
                ...(problem && { problem }),
                ...(kind && { kind }),
                ...(pageParam && { cursor: pageParam }),
                limit: "50",
              },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  summary: () => summary,

  messages: (entityType: string, entityId: string) =>
    queryOptions({
      queryKey: [...keys.all, "messages", entityType, entityId],
      queryFn: ({ signal }) =>
        unwrap(
          api.integration.messages.$get(
            { query: { entityType, entityId, limit: String(ENTITY_MESSAGES_SHOWN) } },
            { init: { signal } },
          ),
        ),
    }),

  /**
   * Each read is written in the audit of the entity: never again in the background, and out of the keys of the queue,
   * which a retry refreshes. Opened again once out of the cache, it is a new read, and a new line of the audit.
   */
  payload: (id: number) =>
    queryOptions({
      queryKey: ["integration-payload", id],
      queryFn: ({ signal }) =>
        unwrap(api.integration.messages[":id"].payload.$get({ param: { id: String(id) } }, { init: { signal } })),
      staleTime: Number.POSITIVE_INFINITY,
      retry: false,
    }),
};

const reconciliationKeys = { all: ["reconciliation"] as const };

/** The issues of the hourly check (ADR-177): no mutation changes them, only the check itself. */
export const reconciliationQueries = {
  ...reconciliationKeys,

  list: ({ kind, entityType, state }: ReconciliationFilters) =>
    infiniteQueryOptions({
      queryKey: [...reconciliationKeys.all, "list", { kind, entityType, state }],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          api.integration.reconciliation.$get(
            {
              query: {
                ...(kind && { kind }),
                ...(entityType && { entityType }),
                ...(state && { state }),
                ...(pageParam && { cursor: pageParam }),
                limit: "50",
              },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  summary: () =>
    queryOptions({
      queryKey: [...reconciliationKeys.all, "summary"],
      queryFn: ({ signal }) => unwrap(api.integration.reconciliation.summary.$get({}, { init: { signal } })),
    }),
};

type Named = Pick<IntegrationError, "messageType" | "aggregateType" | "aggregateId">;

/** The entity a row is about, as short as it stays unique on screen: `catalog.linesheet 0199a000`. */
export const entityName = (row: Named) => `${row.aggregateType} ${row.aggregateId.slice(0, 8)}`;

/** A row by what it is and what it is about: the title of its panel, the name of its checkbox. */
export const messageName = (row: Named) => `${row.messageType} · ${entityName(row)}`;
